<template>
  <div class="checkout-page pb-12">
    <ServerError v-if="ServerError" />
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="blue"
      spinner="mini-spinner"
    />

    <v-snackbar v-model="showSnackBar" color="#3EA56E" right timeout="2000" class="mt-12">
      <div class="d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-check-circle</v-icon>
        <span class="ibmmedium">{{ msg }}</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="showSnackBar = false">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <v-container>
      <v-layout wrap>
        <!-- Page Header -->
        <v-flex xs12 class="mb-8">
          <div class="d-flex align-center">
            <v-btn icon @click="$router.push('/cart')" class="mr-2">
              <v-icon color="#3EA56E">mdi-arrow-left</v-icon>
            </v-btn>
            <h1 class="page-title">Order Confirmation</h1>
          </div>
          <v-divider class="title-divider mt-2"></v-divider>
        </v-flex>

        <v-layout wrap v-if="cart && cart.length > 0">
          <!-- Left Column: Shipping & Promocode & Items -->
          <v-flex xs12 lg7 class="pr-lg-6">
            
            <!-- Address Selection Card -->
            <v-card flat class="confirm-card mb-6 pa-4 pa-sm-6">
              <div class="d-flex align-center justify-space-between mb-4">
                <div class="d-flex align-center">
                  <v-icon color="#3EA56E" class="mr-3">mdi-truck-delivery-outline</v-icon>
                  <h3 class="section-subtitle">Select Delivery Address</h3>
                </div>
                <v-btn text color="#3EA56E" class="text-none ibmsemibold" @click="adddialog = true">
                  + Add New
                </v-btn>
              </div>

              <div v-if="address1 && address1.length > 0">
                <v-radio-group v-model="radios" hide-details>
                  <div v-for="(item, i) in address1" :key="i" 
                       class="address-confirmation-block pa-4 rounded-xl mb-3 d-flex"
                       :class="{'selected-address': radios === item._id}">
                    <v-radio :value="item._id" color="#3EA56E" class="mt-1 align-self-start mr-3" @click="setDefaultAddress(item._id)"></v-radio>
                    <div class="flex-grow-1" style="min-width: 0;">
                      <div class="d-flex justify-space-between align-start">
                        <h4 class="customer-name mb-1 pr-2">{{ item.firstname }} {{ item.lastname }}</h4>
                        <div class="d-flex flex-shrink-0">
                          <v-btn icon small color="#64748b" @click="curItem = JSON.parse(JSON.stringify(item)); editdialog = true"><v-icon size="18">mdi-pencil-outline</v-icon></v-btn>
                          <v-btn icon small color="#ef4444" @click="curId = item._id; dialoge = true"><v-icon size="18">mdi-trash-can-outline</v-icon></v-btn>
                        </div>
                      </div>
                      <p class="address-text mb-1">{{ item.streetAddress }}, {{ item.locality }}, {{ item.city }}</p>
                      <p class="address-text mb-1">{{ item.state }}, {{ item.country }} - Pin: {{ item.pincode }}</p>
                      <p class="address-text ibmmedium color-dark mt-1">
                        <v-icon small class="mr-1">mdi-phone-outline</v-icon> {{ item.delphone }}
                      </p>
                    </div>
                  </div>
                </v-radio-group>
              </div>
              <div v-else class="text-center py-4">
                <span class="ibmmedium grey--text">No delivery addresses found. Please add one.</span>
              </div>
            </v-card>

            <!-- Promocode Card -->
            <v-card flat class="confirm-card mb-6 pa-4 pa-sm-6" v-if="codes && codes.length > 0">
              <div class="d-flex align-center mb-4">
                <v-icon color="#3EA56E" class="mr-3">mdi-ticket-percent-outline</v-icon>
                <h3 class="section-subtitle">Apply Promocode</h3>
              </div>
              
              <div class="promo-input-wrapper d-flex align-center">
                <v-text-field
                  v-model="code1"
                  placeholder="Enter code"
                  outlined
                  dense
                  hide-details
                  class="custom-field flex-grow-1"
                  color="#3EA56E"
                ></v-text-field>
                <v-btn
                  depressed
                  color="#3EA56E"
                  dark
                  class="ml-3 px-8 rounded-lg ibmsemibold"
                  height="40"
                  @click="applyPromocode()"
                  :disabled="!code1"
                >
                  Apply
                </v-btn>
              </div>

              <!-- Available Coupons -->
              <div class="available-coupons mt-4">
                <span class="ibmmedium grey--text text-caption mb-2 d-block">Available Coupons</span>
                <v-layout wrap>
                  <v-flex xs12 v-for="(item, i) in codes" :key="i" class="mb-2">
                    <div class="coupon-tag d-flex align-center justify-space-between pa-3 rounded-lg border-dashed">
                      <div class="d-flex flex-column">
                        <span class="coupon-code ibmsemibold mb-1">{{ item.promoCode }}</span>
                        <span class="coupon-desc">{{ item.description }}</span>
                      </div>
                      <v-btn text small color="#3EA56E" class="ibmsemibold" @click="callPromo(item.promoCode)">
                        Use Code
                      </v-btn>
                    </div>
                  </v-flex>
                </v-layout>
              </div>
            </v-card>

            <!-- Cart Preview Card -->
            <v-card flat class="confirm-card mb-6 mb-lg-0 pa-4 pa-sm-6">
              <div class="d-flex align-center mb-6">
                <v-icon color="#3EA56E" class="mr-3">mdi-cart-check</v-icon>
                <h3 class="section-subtitle">Order Preview ({{ cartCount }} items)</h3>
              </div>
              
              <div class="checkout-items-list">
                <div v-for="(item, i) in cart" :key="i" class="checkout-item-card pa-4 mb-4 rounded-xl border">
                  <div class="d-flex align-center">
                    <template v-if="item.productId && item.productId.photos && item.productId.photos.length > 0">
                      <v-img
                        :src="mediaURL + item.productId.photos[0]"
                        width="80"
                        max-width="80"
                        height="80"
                        contain
                        class="rounded-lg mr-4 bg-light flex-shrink-0"
                      >
                        <template v-slot:placeholder>
                          <v-row class="fill-height ma-0" align="center" justify="center">
                            <v-progress-circular indeterminate color="#3EA56E"></v-progress-circular>
                          </v-row>
                        </template>
                      </v-img>
                    </template>
                    <template v-else>
                      <v-img
                        :src="require('../../../../assets/noimg.png')"
                        width="80"
                        max-width="80"
                        height="80"
                        contain
                        class="rounded-lg mr-4 bg-light flex-shrink-0"
                      ></v-img>
                    </template>
                    <div class="flex-grow-1" style="min-width: 0;">
                      <div class="d-flex justify-space-between align-start">
                        <div>
                          <h4 class="item-name-preview mb-1 cursor-pointer pr-2" @click="$router.push('/productdetailview/' + item.productId.urlname)">
                            {{ item.productId.productname }}
                          </h4>
                        </div>
                        <div class="d-flex flex-column align-end flex-shrink-0">
                          <div class="qty-selector d-flex align-center rounded-lg bg-light px-3 py-1 mb-2">
                            <v-btn
                              icon
                              small
                              class="qty-btn"
                              @click="updateQty(item, item.quantity - 1)"
                              :disabled="item.quantity <= (item.productId.minimumOrderQuantity || 1) || item.productId.status !== 'Active'"
                            >
                              <v-icon size="18">mdi-minus</v-icon>
                            </v-btn>
                            <span class="qty-value mx-4">{{ item.quantity }}</span>
                            <v-btn
                              icon
                              small
                              class="qty-btn"
                              @click="updateQty(item, item.quantity + 1)"
                              :disabled="item.quantity >= (item.productId.maxOrderQuantity || 999) || item.productId.status !== 'Active'"
                            >
                              <v-icon size="18">mdi-plus</v-icon>
                            </v-btn>
                          </div>
                        </div>
                      </div>
                      <div class="d-flex align-center mt-n2">
                        <span class="total-value-small mr-2">₹{{ Number(item.totalOfferPrice).toFixed(2) }}</span>
                        <span v-if="item.totalPrice" class="old-price-small mr-2">₹{{ item.totalPrice }}</span>
                      </div>
                      <div v-if="item.isPromoApplied" class="promo-applied-tag d-flex align-center mt-1">
                        <v-icon x-small color="#10b981" class="mr-1">mdi-check-decagram</v-icon>
                        <span class="green--text text-caption mr-2">Coupon Applied</span>
                        <span v-if="promocodeApplied" style="border: 1px solid #10b981; font-size:10px; color:#10b981; padding: 0 4px; border-radius: 2px;">{{ promocodeApplied }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-flex>

          <!-- Right Column: Order Summary -->
          <v-flex xs12 lg5 mt-6 mt-lg-0>
            <v-card flat class="summary-card sticky-sidebar pa-4 pa-sm-8">
              <h2 class="section-subtitle mb-8 pb-2 border-bottom">Price Summary</h2>
              
              <div class="summary-details" v-if="summary">
                <div class="d-flex justify-space-between mb-4">
                  <span class="summary-label">Standard Price</span>
                  <span class="summary-value">₹{{ summary.totalPrice }}</span>
                </div>
                
                <div class="d-flex justify-space-between mb-4" v-if="summary.totalDiscountAmount > 0">
                  <span class="summary-label">Total Discount</span>
                  <span class="summary-value green--text">- ₹{{ summary.totalDiscountAmount }}</span>
                </div>

  <div class="d-flex justify-space-between mb-4" v-if="summary.totalTaxableAmount !== undefined">
                  <span class="summary-label">Taxable Amount</span>
                  <span class="summary-value">₹{{ summary.totalTaxableAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="summary.totalTaxAmount !== undefined">
                  <span class="summary-label">GST</span>
                  <span class="summary-value">₹{{ summary.totalTaxAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4 pl-8" v-if="summary.sgstAmount !== undefined">
                  <span class="summary-label">SGST</span>
                  <span class="summary-value">₹{{ summary.sgstAmount }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4 pl-8" v-if="summary.cgstAmount !== undefined">
                  <span class="summary-label">CGST</span>
                  <span class="summary-value">₹{{ summary.cgstAmount }}</span>
                </div>


                <div class="d-flex justify-space-between mb-4" v-if="summary.totalPackingCharge > 0">
                  <span class="summary-label">Packing Charges</span>
                  <span class="summary-value">₹{{ summary.totalPackingCharge }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="summary.totalDeliveryCharge > 0">
                  <span class="summary-label">Delivery Charges</span>
                  <span class="summary-value">₹{{ summary.totalDeliveryCharge }}</span>
                </div>

                <div class="d-flex justify-space-between mb-4" v-if="cart && cart.length > 0 && cart[0].productId && cart[0].productId.modeOfDelivery">
                  <span class="summary-label">Mode of Delivery</span>
                  <span class="summary-value green--text">{{ cart[0].productId.modeOfDelivery }}</span>
                </div>
                
                <v-divider class="my-6"></v-divider>
                
                <div class="d-flex justify-space-between mb-10">
                  <span class="total-label">Payable Amount</span>
                  <div class="text-right">
                    <span class="total-value">₹{{ summary.total }}</span>
                    <!-- <p class="tax-inclusive-text">Inclusive of all taxes</p> -->
                  </div>
                </div>

                <div class="checkout-actions">
                  <v-btn
                    block
                    depressed
                    x-large
                    color="#3EA56E"
                    dark
                    height="56"
                    class="place-order-btn mb-4 rounded-xl"
                    @click="validateInput()"
                  >
                    CONTINUE SHIPPING
                  </v-btn>
                  <v-btn
                    block
                    outlined
                    large
                    color="#64748b"
                    class="text-none ibmsemibold rounded-xl"
                    @click="$router.push('/products?MainCategory=Plants')"
                  >
                    Cancel
                  </v-btn>
                </div>

                <div class="secure-checkout-notice d-flex align-center justify-center mt-6">
                  <v-icon x-small color="#64748b" class="mr-2">mdi-shield-check-outline</v-icon>
                  <span>Secure Checkout powered by Razorpay</span>
                </div>
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <!-- Modals -->
        <v-dialog v-model="adddialog" max-width="600px">
          <v-form ref="form">
            <v-card class="pa-2 rounded-xl">
              <v-card-title>
                <span class="headline ibmsemibold">Add New Address</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="adddialog = false"><v-icon>mdi-close</v-icon></v-btn>
              </v-card-title>
              <v-card-text>
                <v-layout wrap>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.firstname" label="Firstname" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.lastname" label="Lastname" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 px-2 mb-2>
                    <v-text-field v-model="user.streetAddress" label="Street Address" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.locality" label="Locality" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.city" label="City" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 px-2 class="d-flex align-center">
                    <v-checkbox
                      v-model="user.isInsideKerala"
                      label="Is Delivery Inside Kerala?"
                      color="#3EA56E"
                      hide-details
                      class="mb-2 mt-2 pt-0"
                      @change="handleInsideKeralaAddChange"
                    ></v-checkbox>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.district" label="District" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.state" label="State" outlined dense :rules="[rules.required]" :disabled="user.isInsideKerala"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.country" label="Country" outlined dense :rules="[rules.required]"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.pincode" type="number" label="Pincode" outlined dense :rules="pinRules"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.delphone" label="Phone" type="number" outlined dense :rules="phoneRules"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md6 lg6 px-2 mb-2>
                    <v-text-field v-model="user.email" label="E-mail" outlined dense :rules="[rules.email]"></v-text-field>
                  </v-flex>
                </v-layout>
              </v-card-text>
              <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="adddialog = false" class="ibmmedium mr-2">Cancel</v-btn>
                <v-btn v-if="!flag" depressed color="#3EA56E" dark @click="addAddress()" class="ibmsemibold px-6">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
        </v-dialog>

        <v-dialog v-model="editdialog" max-width="600px">
          <v-card class="pa-2 rounded-xl">
            <v-card-title>
              <span class="headline ibmsemibold">Edit Address</span>
              <v-spacer></v-spacer>
              <v-btn icon @click="editdialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-card-text>
              <v-layout wrap v-if="curItem">
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.firstname" label="Firstname" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.lastname" label="Lastname" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 px-2 mb-2>
                  <v-text-field v-model="curItem.streetAddress" label="Street Address" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.locality" label="Locality" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.city" label="City" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 px-2 class="d-flex align-center">
                  <v-checkbox
                    v-model="curItem.isInsideKerala"
                    label="Is Delivery Inside Kerala?"
                    color="#3EA56E"
                    hide-details
                    class="mb-2 mt-2 pt-0"
                    @change="handleInsideKeralaEditChange"
                  ></v-checkbox>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.district" label="District" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.state" label="State" outlined dense :rules="[rules.required]" :disabled="curItem.isInsideKerala"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.country" label="Country" outlined dense :rules="[rules.required]"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.pincode" type="number" label="Pincode" outlined dense :rules="pinRules"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.delphone" label="Phone" type="number" outlined dense :rules="phoneRules"></v-text-field>
                </v-flex>
                <v-flex xs12 md6 lg6 px-2 mb-2>
                  <v-text-field v-model="curItem.email" label="E-mail" outlined dense :rules="[rules.email]"></v-text-field>
                </v-flex>
              </v-layout>
            </v-card-text>
            <v-card-actions class="px-6 pb-4">
              <v-spacer></v-spacer>
              <v-btn text @click="editdialog = false" class="ibmmedium mr-2">Cancel</v-btn>
              <v-btn depressed color="#3EA56E" dark @click="editAddress(curItem._id)" class="ibmsemibold px-6">Save</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-dialog width="400px" v-model="dialoge">
          <v-card width="400px" class="rounded-xl">
            <v-card-title class="pt-5 px-5">
              <span class="headline ibmsemibold red--text">Confirm Delete</span>
            </v-card-title>
            <v-card-text class="pa-5 black--text text-left text-body-1">
              Are you sure you want to delete this address?
            </v-card-text>
            <v-card-actions class="pa-5 pt-0">
              <v-spacer></v-spacer>
              <v-btn text color="grey" class="ibmsemibold" @click="dialoge = false">Cancel</v-btn>
              <v-btn color="red" dark depressed class="ibmsemibold px-6" @click="deleteAddress(curId)">Delete</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-layout>
  </v-container>
  </div>
</template>
<script src="https://checkout.razorpay.com/v1/checkout.js"></script>
<script>
import axios from "axios";

export default {
  data() {
    return {
      cart: [],
      promocodeApplied:null,
      address1: [],
      curItem: null,
      cartCount: null,
      appLoading: false,
      ServerError: false,
      showSnackBar: false,
      adddialog: false,
      editdialog: false,
      dialoge: false,
      curId: null,
      flag: null,
      total: [],
      msg: null,
      radios: null,
      orderID: null,
      radios2: false,
      //   address: {
      //     country: null,
      //     state: null,
      //     district: null,
      //     firstname: null,
      //     lastname: null,
      //     pincode: null,
      //     streetAddress: null,
      //     locality: null,
      //     city: null,
      //     delphone: null,

      //     email: null,
      //   },
      code1: null,
      Price: null,
      user: {
        isInsideKerala: true,
        country: null,
        state: 'Kerala',
        district: null,
        firstname: null,
        lastname: null,
        pincode: null,
        streetAddress: null,
        locality: null,
        city: null,
        delphone: null,
        email: null,
        guestId: null,
      },
      codes: [],
      summary: [],
      rules: {
        required: (value) => !!value || "Required.",
        min: (v) => v.length >= 8 || "Min 8 characters",
        minPhone: (value) => value.length == 10 || "Invalid Phone",
        //  pincode: (value) => value.length == 6 || "Please provide valid pincode",
        email: (value) => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "Invalid e-mail.";
        },
      },
      phoneRules: [
        (v) => !!v || "phone is required",
        (v) =>
          (v && v.length <= 10) || "number must be less than 11 characters",
        (v) =>
          (v && v.length >= 10) || "number must be greater than 10 characters",
        (v) => /^\d{10}$/.test(v) || "phone number must be a number",
      ],
      pinRules: [
        (v) => !!v || "Pincode is Required",
        (v) => /^(\d{4}|\d{6})$/.test(v) || "Pincode must be valid",
      ],
    };
  },
  mounted() {
    this.setMetaTags();
    this.getAddress();
    // this.getPromocode();
    // this.getAddress();
  },
  watch: {
    radios() {
      localStorage.setItem("address", this.radios);
      this.getData();
    },
    adddialog(val) {
      if (val) {
        if (typeof this.user.isInsideKerala === 'undefined') {
          this.$set(this.user, 'isInsideKerala', true);
        }
        if (!this.user.state && this.user.isInsideKerala) {
          this.$set(this.user, 'state', 'Kerala');
        }
      }
    },
    editdialog(val) {
      if (val && this.curItem) {
        let isInside = this.curItem.isInsideKerala;
        if (typeof isInside === 'undefined') {
          isInside = (this.curItem.state === 'Kerala' || this.curItem.state === 'kerala');
        }
        this.$set(this.curItem, 'isInsideKerala', isInside);
        if (isInside) {
          this.curItem.state = 'Kerala';
        }
      }
    },
  },
  computed: {
    appUser() {
      return this.$store.state.userData;
    },
    appLogin() {
      return this.$store.state.isLoggedIn;
    },
  },
  //   watch: {
  //     radios() {
  //       localStorage.setItem("address", this.radios);
  //     },
  //   },
  beforeMount() {
    if (!this.appLogin) {
      this.$router.push("/login");
    }
  },
  computed: {
    appLogin() {
      return this.$store.state.isLoggedIn;
    },
  },
  methods: {
    setMetaTags() {
      document.title = "Order Confirmation | KL-06 FARM";
      let metaDescription = document.querySelector('meta[name="description"]');
      const description = "Confirm your order details and checkout securely at KL-06 FARM.";
      if (metaDescription) {
        metaDescription.setAttribute('content', description);
      } else {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        metaDescription.content = description;
        document.head.appendChild(metaDescription);
      }
    },
    handleInsideKeralaAddChange(val) {
      if (val) {
        this.user.state = 'Kerala';
      } else {
        if (this.user.state === 'Kerala') this.user.state = '';
      }
    },
    handleInsideKeralaEditChange(val) {
      if (val) {
        this.curItem.state = 'Kerala';
      } else {
        if (this.curItem.state === 'Kerala') this.curItem.state = '';
      }
    },
    callPromo(item1) {
      console.log("promocode=", item1);
      this.code1 = item1;
    },
    addAddress() {
      if (this.$refs.form.validate()) {
        this.appLoading = true;
        axios({
          method: "post",
          url: "/deliveryaddress/add",
          data: this.user,
          headers: {
            "x-auth-token": localStorage.getItem("token"),
          },
        })
          .then((response) => {
            this.appLoading = false;
            if (response.data.status == true) {
              localStorage.removeItem("address");
              this.msg = response.data.msg;
              this.showsnackbar = true;
              this.adddialog = false;
              this.user = {};
              console.log(response.data.id);
              localStorage.setItem("address", response.data.id);
              console.log("set id");
              this.radios = localStorage.getItem("address");
              console.log("get id");
              this.getAddress();
              console.log("call getaddress");
              //  this.cart = response.data.cart;
            } else {
              this.msg = response.data.msg;
              this.showsnackbar = true;
            }
            // this.cart = response.data.cart;
            // if(this.cart.photo)
            // this.photos = this.cart.productId.photos;
          })
          .catch((err) => {
            this.appLoading = false;
            this.ServerError = true;
            console.log(err);
          });
      }
    },
    editAddress(item) {
      this.curItem.id = item;
      this.appLoading = true;
      axios({
        method: "post",
        url: "/deliveryaddress/edit",
        data: this.curItem,
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = response.data.msg;
            this.showsnackbar = true;
            this.editdialog = false;
            //  this.cart = response.data.cart;
            this.getAddress();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    //delete button
    deleteAddress(item) {
      axios({
        url: "/deliveryaddress/remove",
        method: "POST",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          id: item,
        },
      })
        .then((response) => {
          this.delete = false;
          this.dialoge = false;
          this.appLoading = false;
          if (response.data.status) {
            this.msg = "Delete Sucessfully";
            // this.$router.go(-1)
            this.showsnackbar = true;
            var existingAddress = localStorage.getItem("address");
            if (item == existingAddress) {
              localStorage.removeItem("address");
            }
            this.getAddress();
          } else {
            this.msg = response.data.msg;
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
          appLoading;
        });
    },
    applyPromocode() {
      this.appLoading = true;
      axios({
        method: "post",
        url: "/buynow/Getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          promoCode: this.code1,
          deliveryAddressId: this.radios,
        },
      })
        .then((response) => {
          this.appLoading = false;
                    console.log("11")
          this.summary = response.data;
          this.cart = response.data.cart;
          this.promocodeApplied = response.data.promoCodeDetails.promoCode;
          this.msg = response.data.msg;
          this.showSnackBar = true;
        })
        .catch((err) => {
          this.appLoading = false;

          this.msg = response.data.msg;
          this.showSnackBar = true;
          console.log(err);
        });
    },
    getAddress() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/user/profile",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.address1 = response.data.data.deliveryaddress;
          let defaultIndex = response.data.data.deliveryaddressindex;

          console.log("this.address1", this.address1);

          let targetRadio = null;
          if (defaultIndex && this.address1.some(a => a._id === defaultIndex)) {
            targetRadio = defaultIndex;
          } else if (this.address1.length > 0) {
            targetRadio = this.address1[0]._id;
          }

          if (targetRadio) {
            this.radios = targetRadio;
          } else {
            this.getData();
          }
        })
        .catch((err) => {
          this.appLoading = false;
          console.log(err);
        });
    },
    setDefaultAddress(item) {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/user/edit",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          deliveryaddressindex: item,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            // this.msg = response.data.msg;
            this.msg = "Shipping Address Changed";
            this.showSnackBar = true;
            this.getProfile();
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    getProfile() {
      this.appLoading = true;
      axios({
        method: "GET",
        url: "/user/profile",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          if (response.data.status) {
            this.appLoading = false;
            this.radios = response.data.data.deliveryaddressindex;
            localStorage.setItem("address", this.radios);
            // for (var i = 0; i < this.address.length; i++) {
            //   if (
            //     response.data.data.deliveryaddressindex == this.address[i]._id
            //   ) {
            //     this.defaultAddress = this.address[i];
            //     // this.defaultAddress =localStorage.getItem("address");
            //     // console.log(this.defaultAddress)
            //   }
            // }
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    userData() {
      this.appLoading = true;
      axios({
        method: "get",
        url: "/user/info",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          this.cart = response.data.cart;
          if (this.cart.photo) this.photos = this.cart.productId.photos;
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    validateInput() {
      if (!this.radios || !this.address1) {
        this.msg = "Please select a delivery address";
        this.showSnackBar = true;
        return;
      } else this.placeorder();
    },
    placeorder() {
      this.appLoading = true;
      axios({
        method: "POST",
        url: "/customer/buynow",
        data: {
          deliveryAddressId: this.radios,
          orderTotal: this.summary.total,
          promoCode: this.code1,
        },
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status) {
            // paymnt
            var PHNO = response.data.phone;
            window.id = response.data.id;
            window.orderid = response.data.data.order_id;
            // window.orderid = response.data.orderID;

            setTimeout(function () {}, 10000);
            var options = {
              // key: "rzp_test_ShFpH9VHzFyMxv",
              key: "rzp_live_ShGFZKpjBK09Gr",
              amount: Number(this.amount * 100),
              name: this.user.firstname,
              // name: "Sabu",

              currency: "INR",
              description: "KLO6",
              image: "https://jolly-volhard-bc2f0b.netlify.com/favicon.ico",
              order_id: orderid,

              handler: function (response) {
                // location.href =
                //   "http://localhost:8081/shopping/paywaitScreen/" +
                //   id +
                //   "/" +
                //   orderid;

                location.href = "/PaywaitScreen/" + id + "/" + orderid;

                +(
                  // this.productId;
                  0
                );
              },

              prefill: {
                // name: this.user.firstname,
                email: response.data.email,
                contact: response.data.phone,
                // contact:PHNO
              },

              theme: {
                color: "#3EA56E",
              },
              // modal: {
              //   ondismiss: function () {
              //     location.href = "https://parambikulam.org/";
              //   },
              // },
              // callback_url:
              //   "http://parambikulam.org/planYourVisit/paywaitScreen?id =" +
              //   response.data.paymentdata.txnid,
              // redirect: true,
            };
            var rzp1 = new Razorpay(options);
            rzp1.open();
            // this.payid = response.data.donationdata._id;
            //end paymnr

            // this.msg = "Order Place Successully";
            // this.snackbar = true;
            // this.id = response.data.id;
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
    // getPromocode() {
    //   this.appLoading = true;
    //   axios({
    //     method: "get",
    //     url: "/promocode/getlist",
    //     headers: {
    //       "x-auth-token": localStorage.getItem("token"),
    //     },
    //   })
    //     .then((response) => {
    //       this.appLoading = false;
    //       this.codes = response.data.data;
    //     })
    //     .catch((err) => {
    //       this.appLoading = false;
    //       // this.ServerError = true;
    //       console.log(err);
    //     });
    // },
    getData() {
      this.appLoading = true;
      axios({
        method: "Post",
        url: "/buynow/Getlist",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        data: {
          deliveryAddressId: this.radios,
        }
      })
        .then((response) => {
          this.appLoading = false;
          this.cart = response.data.cart;
          this.codes = response.data.availablePromoCodes;
          this.cartCount = response.data.cart_length;
          this.Price = response.data.cart[0];
          this.summary = response.data;
          if (this.cart.photo) this.photos = this.cart.productId.photos;
          this.total = response.data.total;
        })
        .catch((err) => {
          this.appLoading = false;
          // this.ServerError = true;
          console.log(err);
        });
    },
    updateQty(item, newQty) {
      this.appLoading = true;
      var data = {
        productId: item.productId._id,
        quantity: newQty,
      };
      var headers1 = {};
      if (this.appLogin) {
        headers1 = {
          "x-auth-token": localStorage.getItem("token"),
        };
      } else {
        data["guestid"] = localStorage.getItem("guestId");
      }

      axios({
        method: "post",
        url: "/buynow",
        data: data,
        headers: headers1,
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status == true) {
            this.msg = "Quantity updated";
            this.showSnackBar = true;
            this.getData(); // Refresh the list with updated quantity
          } else {
            this.msg = response.data.msg;
            this.showSnackBar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.log(err);
        });
    },
  },
};
</script>

<style scoped>
.checkout-page {
  background-color: #f8fafc;
  min-height: 100vh;
}

.page-title {
  font-family: "ibmsemibold", sans-serif;
  font-size: 26px;
  color: #1a202c;
  margin: 0;
}

.title-divider {
  border-color: #3EA56E;
  max-width: 60px;
  border-width: 3px;
  opacity: 1;
}

.section-subtitle {
  font-family: "ibmsemibold", sans-serif;
  font-size: 18px;
  color: #1a202c;
  margin: 0;
}

.confirm-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px !important;
}

.address-confirmation-block {
  background-color: #ffffff;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
}

.address-confirmation-block.selected-address {
  border: 2px solid #3ea56e;
  background-color: #f0fdf4;
}

.customer-name {
  font-family: "ibmsemibold", sans-serif;
  font-size: 16px;
  color: #1a202c;
}

.address-text {
  font-family: "sofialight", sans-serif;
  font-size: 14px;
  color: #475569;
}

.coupon-tag {
  background-color: #f8fafc;
  border: 1px dashed #cbd5e1;
}

.coupon-code {
  font-size: 14px;
  color: #3EA56E;
  letter-spacing: 0.5px;
}

.coupon-desc {
  font-family: "sofialight", sans-serif;
  font-size: 12px;
  color: #64748b;
}

.item-name-preview {
  font-family: "ibmmedium", sans-serif;
  font-size: 14px;
  color: #1e293b;
}

.checkout-item-card {
  transition: transform 0.2s ease;
  border-color: #f1f5f9 !important;
}

.checkout-item-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.qty-selector {
  background-color: #f1f5f9;
}

.qty-btn {
  background-color: #ffffff !important;
  color: #3EA56E !important;
  border-radius: 8px !important;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.qty-btn:hover:not(:disabled) {
  background-color: #e2e8f0 !important;
}

.qty-value {
  font-family: "ibmsemibold", sans-serif;
  font-size: 15px;
  min-width: 24px;
  text-align: center;
  color: #1e293b;
}

.total-value-small {
  font-family: "ibmsemibold", sans-serif;
  font-size: 16px;
  color: #3EA56E;
}

.old-price-small {
  text-decoration: line-through;
  font-size: 13px;
  color: #94a3b8;
}

.summary-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 20px !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05) !important;
}

.sticky-sidebar {
  position: sticky;
  top: 100px;
}

.summary-label {
  font-family: "sofialight", sans-serif;
  font-size: 15px;
  color: #64748b;
}

.summary-value {
  font-family: "ibmmedium", sans-serif;
  font-size: 15px;
  color: #1e293b;
}

.total-label {
  font-family: "ibmsemibold", sans-serif;
  font-size: 18px;
  color: #1a202c;
}

.total-value {
  font-family: "ibmsemibold", sans-serif;
  font-size: 24px;
  color: #3EA56E;
}

.tax-inclusive-text {
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
}

.secure-checkout-notice {
  font-family: "sofialight", sans-serif;
  font-size: 12px;
  color: #64748b;
}

.bb-1 {
  border-bottom: 1px solid #f1f5f9;
}

.bg-light {
  background-color: #f8fafc;
}

::-webkit-scrollbar {
  width: 5px !important;
}
::-webkit-scrollbar-track {
  background: #f1f5f9 !important;
}
::-webkit-scrollbar-thumb {
  background: #3ea56e !important;
  border-radius: 5px !important;
}

@media (max-width: 960px) {
  .sticky-sidebar {
    position: static;
  }
}

@media (max-width: 600px) {
  .page-title {
    font-size: 22px;
  }
  .total-value {
    font-size: 20px;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>